import { OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo, useState } from 'react';
import RoutingRules from 'routing-ui/routing-rules/components/RoutingRules';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { H2 } from 'UIComponents/elements/headings';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
// @ts-expect-error module not typed
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getClickedMoveToInboxCancel, getClickedMoveToInboxConfirm, getMoveToInboxAddedComment } from '../../usage-tracking/utils/threadInteraction';
import { useConversationHandoffForm } from '../hooks/useConversationHandoffForm';
import { getRoutingRuleTypeForEventTracking } from '../_internal/operators/getRoutingRuleTypeForEventTracking';
import { EMAIL_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
import { mapGenericChannelIdToChannelType } from 'conversations-thread-data/generic-channels/public/operators';
import { useCountOfConversationsOnAssociatedTicket } from 'conversations-thread-data/conversation-ticket-associations/public/hooks';
import { isInboxHelpdesk } from 'conversations-internal-schema/inbox/operators/inboxGetters';
import MoveToInboxAddComment from './MoveToInboxAddComment';
import MoveToInboxAddTicketSubject from './MoveToInboxAddTicketSubject';
import { useRoutingRules } from '../hooks/useRoutingRules';
import MoveToInboxSelectInbox from './MoveToInboxSelectInbox';
import { useHandleFormSubmit } from '../hooks/useHandleFormSubmit';
import { useConversationInfo } from '../hooks/useConversationInfo';
import MoveToInboxPipelineAndStatus from './MoveToInboxPipelineAndStatus';
import { useSelector } from 'react-redux';
import { getIsUngatedForHelpDeskRoutingScopeFiltering, getHasHelpDeskRoutingConfigEditAccess } from 'conversations-thread-data/auth/public/selectors';
import { HELPDESK } from 'ui-addon-conversations-settings/workspaces/constants/workspaces';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MoveToInbox = ({
  onConfirm,
  onReject,
  properties
}) => {
  const [shouldDisableRoutingRules, setShouldDisableRoutingRules] = useState(false);
  const {
    assigneeId,
    associatedTicketIds,
    conversationId,
    currentInboxId,
    genericChannelId,
    inboxData,
    loadingConversationInfo,
    threadId
  } = useConversationInfo();
  const isUngatedForHelpDeskRoutingScopeFiltering = useSelector(getIsUngatedForHelpDeskRoutingScopeFiltering);
  const hasHelpDeskRoutingConfigEditAccess = useSelector(getHasHelpDeskRoutingConfigEditAccess);
  const threadCurrentlyInHelpdesk = useMemo(() => {
    const threadsCurrentInbox = inboxData && inboxData.find(inbox => inbox.id === currentInboxId);
    return Boolean(threadsCurrentInbox && isInboxHelpdesk(threadsCurrentInbox));
  }, [inboxData, currentInboxId]);
  const {
    totalConversationsCount,
    loading: conversationCountLoading
  } = useCountOfConversationsOnAssociatedTicket({
    /*  @ts-expect-error Jun-5-2024, 19:34UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    conversationId,
    skip: threadCurrentlyInHelpdesk
  });
  const {
    handleFormSubmitCompleted,
    handleFormSubmitError
  } = useHandleFormSubmit({
    onConfirm,
    onReject,
    threadId
  });
  const {
    setFieldValue: {
      setComment,
      setInbox,
      setRoutingRuleDefinition,
      setSubject,
      setPipelineInformation
    },
    submitForm,
    isValid,
    comment,
    pipelineInformation,
    selectedInboxId,
    routingRuleDefinition,
    isToHelpdesk,
    subject,
    shouldCreateNewTicket
  } = useConversationHandoffForm({
    /*  @ts-expect-error Jun-5-2024, 19:34UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    associatedTicketIds,
    genericChannelId,
    threadId: `${threadId}`,
    options: {
      onCompleted: handleFormSubmitCompleted,
      onError: handleFormSubmitError
    }
  });
  const handleFormOnSubmit = evt => {
    evt.preventDefault();
    submitForm();
  };
  const handleOnConfirmMove = () => {
    if (comment.length > 0) {
      trackEvent(getMoveToInboxAddedComment());
    }
    const type = getRoutingRuleTypeForEventTracking(routingRuleDefinition);
    trackEvent(getClickedMoveToInboxConfirm({
      type
    }));
    submitForm();
  };
  const handleOnCancel = () => {
    trackEvent(getClickedMoveToInboxCancel());
    onReject();
  };
  const {
    handleRoutingRuleOnOpen,
    baseRoutingRulesProps,
    userInfoDataIsLoading,
    canKeepAssignee
  } = useRoutingRules({
    assigneeId,
    selectedInboxId,
    routingRuleDefinition,
    setRoutingRuleDefinition
  });
  const loading = loadingConversationInfo || userInfoDataIsLoading || conversationCountLoading;
  const isInboxToInboxMove = !threadCurrentlyInHelpdesk && !isToHelpdesk;
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "move-to-inbox-dialog",
    width: 550,
    children: [/*#__PURE__*/_jsx(UIDialogHeader, {
      children: /*#__PURE__*/_jsxs(H2, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.moveToInboxPrompt.heading"
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: handleOnCancel
        })]
      })
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [loading ? /*#__PURE__*/_jsx(UILoadingOverlay, {
        contextual: true
      }) : null, /*#__PURE__*/_jsxs(UIForm, {
        onSubmit: handleFormOnSubmit,
        children: [/*#__PURE__*/_jsx(UIFormControl, {
          className: "m-bottom-4",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.inputTitle"
          }),
          required: true,
          children: /*#__PURE__*/_jsx(MoveToInboxSelectInbox
          /*  @ts-expect-error Jun-5-2024, 19:34UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */, {
            currentInboxId: currentInboxId,
            inboxData: inboxData,
            loading: loading,
            selectedInboxId: selectedInboxId,
            setShouldDisableRoutingRules: setShouldDisableRoutingRules,
            setRoutingRuleDefinition: setRoutingRuleDefinition,
            setInbox: setInbox,
            threadCurrentlyInHelpdesk: threadCurrentlyInHelpdesk,
            totalConversationsCount: totalConversationsCount
          })
        }), shouldCreateNewTicket && /*#__PURE__*/_jsx(MoveToInboxAddTicketSubject, {
          subject: subject,
          setSubject: setSubject
        }), isToHelpdesk && /*#__PURE__*/_jsx(MoveToInboxPipelineAndStatus, {
          properties: properties,
          setPipelineInformation: setPipelineInformation,
          pipelineInformation: pipelineInformation
        }), baseRoutingRulesProps && !shouldDisableRoutingRules && /*#__PURE__*/_jsx(RoutingRules, Object.assign({}, baseRoutingRulesProps, {
          disabled: !selectedInboxId,
          FormControl: UIFormControl,
          showAnyUserOption: true,
          showKeepAssigneeOption: !isToHelpdesk,
          showOwnerOption: isInboxToInboxMove,
          disableKeepAssigneeOption: !canKeepAssignee,
          onOpenChange: handleRoutingRuleOnOpen,
          use: mapGenericChannelIdToChannelType(genericChannelId),
          isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForHelpDeskRoutingScopeFiltering,
          hasEditAccess: hasHelpDeskRoutingConfigEditAccess,
          workSpace: threadCurrentlyInHelpdesk || isToHelpdesk ? HELPDESK : undefined
        })), /*#__PURE__*/_jsx(MoveToInboxAddComment, {
          comment: comment,
          setComment: setComment
        })]
      }), /*#__PURE__*/_jsxs(UIList, {
        className: "m-top-4",
        children: [isToHelpdesk && !shouldCreateNewTicket && /*#__PURE__*/_jsxs("div", {
          className: "display-flex",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: OZ,
            name: "success",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.toHelpDeskHelpTexts.withTicketHelpText"
          })]
        }), threadCurrentlyInHelpdesk && /*#__PURE__*/_jsxs("div", {
          className: "display-flex",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: OZ,
            name: "success",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.toHelpDeskHelpTexts.fromHelpDeskText"
          })]
        }), isInboxToInboxMove && /*#__PURE__*/_jsxs("div", {
          className: "display-flex",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: OZ,
            name: "success",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.helpTextFour"
          })]
        }), shouldDisableRoutingRules && /*#__PURE__*/_jsxs("div", {
          className: "display-flex",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: OZ,
            name: "success",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.toHelpDeskHelpTexts.unassignText"
          })]
        }), genericChannelId !== EMAIL_GENERIC_CHANNEL_ID && !shouldDisableRoutingRules && /*#__PURE__*/_jsxs("div", {
          className: "display-flex",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: OZ,
            name: "success",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.helpTextFive"
          })]
        })]
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "move-to-inbox-confirm-button",
        use: "primary",
        disabled: !isValid,
        onClick: handleOnConfirmMove,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.moveToInboxPrompt.confirm"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        use: "tertiary-light",
        onClick: handleOnCancel,
        "data-test-id": "move-to-inbox-prompt-cancel-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.moveToInboxPrompt.cancel"
        })
      })]
    })]
  });
};
MoveToInbox.displayName = 'MoveToInbox';
export default MoveToInbox;